import { getPageFromHash } from "@client/content/controls/Pagination/lib";
import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { goTo } from "@client/lib/navigation";
import { type EntityRef } from "@client/types";
import { type AppState } from "@client/types/AppState";
import { type AnyAction, type Dispatch } from "redux";
import { initAction } from "../actions";
import cartApi from "../Cart/api";
import { type Cart } from "../Cart/types";
import api from "./api";
import { localStateSelector, makeProductPageRequestSelector } from "./selectors";
import { ProductList } from "./types";

export enum Actions {
  InitBegin = "MODULE_PRODUCT_LIST_INIT_BEGIN",
  InitFailure = "MODULE_PRODUCT_LIST_INIT_FAILURE",
  InitSuccess = "MODULE_PRODUCT_LIST_INIT_SUCCESS",

  AddToCartBegin = "MODULE_PRODUCT_ADD_TO_CART_BEGIN",
  AddToCartFailure = "MODULE_PRODUCT_ADD_TO_CART_FAILURE",
  AddToCartSuccess = "MODULE_PRODUCT_ADD_TO_CART_SUCCESS",

  FetchPageBegin = "MODULE_PRODUCT_LIST_FETCH_PAGE_BEGIN",
  FetchPageFailure = "MODULE_PRODUCT_LIST_FETCH_PAGE_FAILURE",
  FetchPageSuccess = "MODULE_PRODUCT_LIST_FETCH_PAGE_SUCCESS",

  SetFilter = "MODULE_PRODUCT_LIST_SET_FILTER"
}

export type AddToCartArgs = EntityRef & {
  productId: string;
  quantity: number;
  goToCheckout: boolean;
};
export type AddToCartSuccessActionData = Cart.Api.AddResponse & {
  notification: any;
};
export const addToCart = apiAction<AppState, AddToCartArgs, Cart.Api.AddResponse, AddToCartSuccessActionData>({
  types: {
    begin: Actions.AddToCartBegin,
    failure: Actions.AddToCartFailure,
    success: [
      Actions.AddToCartSuccess,
      ({ getState, params, res }) => {
        params.goToCheckout && goTo(res.checkoutUrl);
      }
    ]
  },
  action: async (state: AppState, dispatch: Dispatch<AnyAction>, args: AddToCartArgs) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);
    const product = localState.items.find((p) => p.id === args.productId);
    if (!product || !product.productVariantId) throw new Error(`Unknown product id: ${args.productId} or no variant`);

    return cartApi.add(apiInfo, {
      variantId: product.productVariantId,
      quantity: args.quantity,
      resetCart: false
    });
  }
});

export type FetchPageBeginActionData = EntityRef & {
  page: number;
};
export type FetchPageSuccessActionData = ProductList.Api.GetProductsResponse;
export const fetchPage = apiAction<AppState, FetchPageBeginActionData, ProductList.Api.GetProductsResponse>({
  types: {
    begin: Actions.FetchPageBegin,
    failure: Actions.FetchPageFailure,
    success: Actions.FetchPageSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: FetchPageBeginActionData) => {
    const request = makeProductPageRequestSelector(args.page)(state, args);
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.getPage(apiInfo, request);
  }
});

export const search = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, { id: ref.id });
  if (!localState.isFetchingPage) {
    dispatch(fetchPage({ ...ref, page: 1 }));
  }
};

export type InitBeginActionData = EntityRef & {
  page: number;
};
export type InitSuccessActionData = ProductList.Api.InitResponse;
export const _init = initAction<AppState, InitBeginActionData, ProductList.Api.InitResponse>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: InitBeginActionData) => {
    const request = makeProductPageRequestSelector(args.page)(state, args);
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.init(apiInfo, request);
  }
});

export const init = (args: EntityRef) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === "None") {
    dispatch(_init({ ...args, page: getPageFromHash() ?? 1 }));
  }
};

export type SetFilterActionData = EntityRef & {
  name: string;
  value: any;
};
export const setFilter =
  (id: string, name: string, value: any) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    dispatch({
      type: Actions.SetFilter,
      data: <SetFilterActionData>{
        id,
        name,
        value
      }
    });
  };
